import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ClientHeader } from '@/components/layout/client-header';
import { ClientSidebar } from '@/components/layout/client-sidebar';
import { FeedbackWidget } from '@/components/layout/feedback-widget';
import { getTokenStore } from '@shared/api/token-store';
import { useMe } from '@shared/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';
import {
  clearPwaBadge,
  requestAndRegisterPushToken,
  setupForegroundNotificationListener,
} from '@shared/services/firebase-push';
import { cn } from '@/lib/cn';

export function ClientLayout() {
  const { pathname } = useLocation();
  const hasToken = !!getTokenStore().getAccessToken();
  const { data: me, isLoading, isError } = useMe();
  const hydrated = useAuthStore((s) => s.hydrated);
  const mainRef = useRef<HTMLElement>(null);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('client_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('client_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const isChatThread = /^\/client\/chat\/.+/.test(pathname);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [pathname]);

  useEffect(() => {
    if (hasToken) {
      void requestAndRegisterPushToken();
      const cleanup = setupForegroundNotificationListener();
      return () => cleanup();
    }
  }, [hasToken]);

  useEffect(() => {
    if (isChatThread) {
      clearPwaBadge();
    }
  }, [isChatThread]);

  if (!hydrated || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  if (isError) {
    getTokenStore().clear();
    return <Navigate to="/login" replace />;
  }

  // Role gate — ASSOCIATE / FRANCHISE users are redirected to associate workspace.
  if (me && (me.role === 'ASSOCIATE' || me.role === 'FRANCHISE')) {
    return <Navigate to="/associate/transactions" replace />;
  }

  return (
    <div className="flex h-screen h-[100dvh] min-h-0 w-full overflow-hidden bg-gray-50">
      {/* Desktop Sidebar */}
      <ClientSidebar isCollapsed={isCollapsed} onToggleCollapse={toggleCollapsed} />

      {/* Main Content View */}
      <div className="flex min-w-0 flex-1 flex-col h-full min-h-0 overflow-hidden">
        <ClientHeader />
        <main
          ref={mainRef}
          className={cn(
            'min-w-0 flex-1 overflow-y-auto flex flex-col min-h-0 md:pb-0',
            isChatThread ? 'pb-0' : 'pb-16',
          )}
        >
          <Outlet />
        </main>
        <FeedbackWidget />
      </div>

      {/* Mobile Bottom Navigation Bar */}
      {!isChatThread && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 h-16 bg-white border-t border-gray-200/80 flex items-center justify-around px-2 shadow-[0_-4px_10px_rgba(0,0,0,0.04)] select-none">
          <NavLink
            to="/client/home"
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1 w-14 h-full text-[10px] font-bold transition-all duration-150',
                isActive ? 'text-brand-700' : 'text-gray-400 hover:text-gray-650',
              )
            }
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
              />
            </svg>
            <span>Home</span>
          </NavLink>

          <NavLink
            to="/client/services"
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1 w-14 h-full text-[10px] font-bold transition-all duration-150',
                isActive ? 'text-brand-700' : 'text-gray-400 hover:text-gray-650',
              )
            }
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v5.25c0 .414.336.75.75.75z"
              />
            </svg>
            <span>Services</span>
          </NavLink>

          <NavLink
            to="/client/chat"
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1 w-14 h-full text-[10px] font-bold transition-all duration-150',
                isActive ? 'text-brand-700' : 'text-gray-400 hover:text-gray-650',
              )
            }
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span>Chat</span>
          </NavLink>

          <NavLink
            to="/client/menu"
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1 w-14 h-full text-[10px] font-bold transition-all duration-150',
                isActive ? 'text-brand-700' : 'text-gray-400 hover:text-gray-650',
              )
            }
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
            <span>Menu</span>
          </NavLink>
        </nav>
      )}
    </div>
  );
}
